import React from "react";
import * as XLSX from "xlsx";
import { LdOption } from "@liquid/react";
import {
  DOWNLOAD_OPTIONS,
  EXPORT_HEADERS,
  EXPORT_HEADERS_STOP_CODEN,
  KEYS_TO_COLLECT,
  KEYS_TO_COLLECT_STOP_CODEN,
  MAX_GRNA_FOR_PRIMER,
} from "@src/constants";
import { DownloadExcelButtonProps } from "@src/types";
import { StyledDropdown, StyledDropdownWrapper } from "./DownloadExcel.style";
import { LdSelectCustomEvent } from "@emdgroup-liquid/liquid";
import { timestampForFileName } from "@src/utils/utils";
import {
  clearAllNotifications,
  showNotification,
} from "@src/hooks/useNotification";
import { excelTopLink } from "@src/config";

type DownloadType = "all" | "selected" | "selectedPrimer";
const DownloadExcelButton: React.FC<DownloadExcelButtonProps> = ({
  data,
  selectedMode,
  selectedCBEType,
  onPrimerDesignSelected,
  isGenomeSpecific = true,
}: any) => {
  let options = isGenomeSpecific
    ? DOWNLOAD_OPTIONS
    : DOWNLOAD_OPTIONS.filter((o) => o.value !== "selectedPrimer");
  let HEADERS: string[] = [];
  let KEYS: string[] = [];
  if (selectedMode === "codon") {
    HEADERS = EXPORT_HEADERS_STOP_CODEN;
    KEYS = KEYS_TO_COLLECT_STOP_CODEN;
  } else if (selectedMode === "base") {
    HEADERS = EXPORT_HEADERS;
    KEYS = KEYS_TO_COLLECT;
  }

  const downloadExcel = (downloadType: DownloadType) => {
    let dataToDownload: any[] = data;
    if (downloadType) {
      if (downloadType === "selected" || downloadType === "selectedPrimer") {
        dataToDownload = data?.filter((item: any) => item.selected);
        if (dataToDownload && dataToDownload.length === 0) {
          clearAllNotifications();
          showNotification({
            content: `No gRNA selected to ${downloadType === "selected" ? "download" : "design primer"}`,
            type: "alert",
          });
          return;
        }

        if (
          downloadType === "selectedPrimer" &&
          dataToDownload &&
          dataToDownload.length > MAX_GRNA_FOR_PRIMER
        ) {
          clearAllNotifications();
          showNotification({
            content: `Maximum ${MAX_GRNA_FOR_PRIMER} allowed to Design Primer`,
            type: "alert",
          });
          return;
        }
      }

      if (downloadType === "selectedPrimer") {
        // invoke primer design modal popup
        if (
          onPrimerDesignSelected &&
          typeof onPrimerDesignSelected === "function"
        ) {
          onPrimerDesignSelected();
          return;
        }
        return; // if selected primer, exit here
      }

      // Create a hyperlink cell for the very top row (A1)
      const hyperlinkText = excelTopLink.text;
      const hyperlinkUrl = excelTopLink.link_url;
      const topLinkRow: any[] = [
        {
          t: "s",
          v: hyperlinkText,
          l: { Target: hyperlinkUrl, Tooltip: "Click to open" },
        },
      ];

      const dataValueExcel = createUpdateData(dataToDownload, KEYS);
      const dataExcleWithHeaders: any[][] = selectedCBEType
        ? [
            topLinkRow,
            ["CBE Type:", selectedCBEType],
            HEADERS,
            ...dataValueExcel,
          ]
        : [topLinkRow, HEADERS, ...dataValueExcel];
      const workBook = XLSX.utils.book_new();
      const workSheet = XLSX.utils.aoa_to_sheet(dataExcleWithHeaders);
      XLSX.utils.book_append_sheet(workBook, workSheet, "sheet1");
      XLSX.writeFile(workBook, `base_editor_${timestampForFileName()}.xlsx`);
    }
  };

  return (
    <StyledDropdownWrapper>
      <StyledDropdown
        placeholder={isGenomeSpecific ? "Download gRNA & Primer(s)" : "Download gRNA"}
        key={options.length > 0 ? options[0]?.value : ""}
        onLdchange={(ev: LdSelectCustomEvent<any[]>) =>
          downloadExcel(ev.detail[0])
        }
        name="downloadType"
      >
        {options.map((option) => (
          <LdOption key={option.value} value={option.value}>
            {option.label}
          </LdOption>
        ))}
      </StyledDropdown>
    </StyledDropdownWrapper>
  );
};

const createUpdateData = (data: any[], KEYS: string[]) => {
  const dataForExcel: any = data?.map((obj: any) =>
    KEYS.flatMap((key: string) => {
      return key === "off_target_analysis"
        ? obj[key]?.split("#").map(Number)
        : obj[key];
    })
  );
  return dataForExcel;
};

export { DownloadExcelButton };
